import { SITE_URL } from "../../../components/Seo/Seo";

// One source of truth for the glossary: each term generates a rendered block
// pair (term + definition) AND a DefinedTerm in the DefinedTermSet schema.

const terms = [
  {
    term: "Active listing",
    def: "A property currently for sale and available on the market. Compared against recently sold listings in a comparative market analysis.",
  },
  {
    term: "Comparable sales (comps)",
    def: "Recently sold homes similar to a subject property in location, size, age, and condition — the basis for pricing. Sold comps matter far more than active (still-for-sale) listings.",
  },
  {
    term: "Comparative market analysis (CMA)",
    def: "An estimate of a home's market value built from recent comparable sales and current competition, used to set or evaluate a list price.",
  },
  {
    term: "Days on market (DOM)",
    def: "How long a listing has been actively for sale. A high DOM signals to buyers that something may be wrong, which can become its own drag on the sale.",
  },
  {
    term: "List price",
    def: "The advertised asking price of a home. Distinct from the sale price, which is what a buyer actually pays.",
  },
  {
    term: "Price position",
    def: "Where a home's list price sits relative to comparable sales and active competition — under, at, or over market. One of the core reads in a Listino listing prep.",
  },
  {
    term: "Price reduction",
    def: "A lowering of the list price. One decisive reduction into the range of sold comps usually outperforms a series of small cuts that trail the market.",
  },
  {
    term: "Listing description",
    def: "The written marketing copy for a listing. Strong descriptions lead with the single best feature and sell the lifestyle rather than repeating facts the photos already show.",
  },
  {
    term: "Lead photo",
    def: "The first image buyers see in search results. Because most buyers decide whether to click based on it, it is the single most important photo in a listing.",
  },
  {
    term: "Photo sequencing",
    def: "The order in which listing photos are presented. A deliberate sequence (exterior, main living space, kitchen, primary suite, and so on) tells the home's story and holds attention.",
  },
  {
    term: "Virtual staging",
    def: "Digitally adding furniture and decor to photos of empty rooms. Generally must be disclosed as virtually staged per MLS rules.",
  },
  {
    term: "AI photo touch-ups",
    def: "Photorealistic cleanup of real listing photos — decluttering, greening a lawn, brightening an overcast exterior — without misrepresenting the property. Disclosure norms vary by MLS.",
  },
  {
    term: "Listing appeal",
    def: "How attractive a listing is to buyers across photos, description, and presentation — independent of price. One of the core reads in a Listino listing prep.",
  },
  {
    term: "Market competitiveness",
    def: "How a listing stacks up against the other homes a buyer is actively considering in the same area and price range.",
  },
  {
    term: "Listing readiness",
    def: "How prepared a property is to go on the market — pricing, presentation, and marketing — assessed in Pre-Listing Prep before going live.",
  },
  {
    term: "Pre-listing",
    def: "The period before a home is publicly listed, when pricing, repairs, staging, and marketing strategy are decided.",
  },
  {
    term: "Showing",
    def: "An in-person visit by a prospective buyer. Online views that don't convert to showings usually point to a price or lead-photo problem.",
  },
  {
    term: "For sale by owner (FSBO)",
    def: "A home sold without a listing agent. FSBO sellers handle pricing, marketing, and negotiation themselves.",
  },
  {
    term: "Listing appointment",
    def: "The meeting where an agent presents their pricing and marketing plan to a prospective seller in order to win the listing.",
  },
  {
    term: "Expired listing",
    def: "A listing whose agreement ended without a sale. Usually a sign of a price, presentation, or promotion problem rather than an unsellable home.",
  },
  {
    term: "Coming soon",
    def: "A pre-launch status that builds anticipation before a listing goes fully active, concentrating early buyer attention.",
  },
  {
    term: "Syndication",
    def: "Distribution of a listing from the MLS to portals and partner sites so it reaches the widest possible buyer audience.",
  },
  {
    term: "Net proceeds",
    def: "What a seller actually keeps after the sale price minus mortgage payoff, commissions, and closing costs. Optimizing a listing aims to improve net proceeds, not just speed.",
  },
];

/** @param {string} s @returns {string} */
const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const article = {
  slug: "glossary",
  cluster: "compare",
  title: "Real Estate Listing Glossary: Terms Every Agent and Seller Should Know",
  metaTitle: "Real Estate Listing Glossary | Listino",
  description:
    "Plain-English definitions of the listing terms that matter — comps, CMA, days on market, price position, photo sequencing, and more.",
  audience: "Sellers & agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "6 min read",
  tldr: "Plain-English definitions of the real estate listing terms that come up most when pricing, marketing, and selling a home — from comparable sales and CMAs to days on market, price position, and photo sequencing.",
  blocks: [
    {
      type: "p",
      spans: [
        "The vocabulary of listings, defined simply. Each term links to the practical guide where it matters most.",
      ],
    },
    ...terms.flatMap((t) => [
      { type: "h3", id: slugify(t.term), text: t.term },
      { type: "p", spans: [t.def] },
    ]),
  ],
  extraJsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Real Estate Listing Glossary",
      url: `${SITE_URL}/resources/glossary`,
      hasDefinedTerm: terms.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.def,
        url: `${SITE_URL}/resources/glossary#${slugify(t.term)}`,
      })),
    },
  ],
  related: ["what-is-a-cma", "is-my-house-overpriced", "best-order-for-listing-photos"],
};

export default article;
